import { Environment, Environments } from '@telstra-connect/config/environments';
import { getCurrentEnvironment, Config } from '@telstra-connect/config/config';
import { ApplicationsFlags } from '@telstra-connect/channel-care/state/subscription-state';

type Flag = {
    [key in Environments]: boolean;
};

export interface IFlag extends Flag { }

export type FlagData<TFlagProps> = {
    [flagProp in keyof TFlagProps]: IFlag
}

export const flagIsSet = (flag: IFlag, environment: Environment) => {
    return flag[environment] === true;
};

export const isFlagOn = (flag: boolean): boolean => {
    return flag === true;
};

export const isFlagOff = (flag: boolean): boolean => {
    return flag === false;
};

type GetStateReturn =
    Partial<{
        [appName in keyof ApplicationsFlags]?: Partial<ApplicationsFlags[appName]>
    }>;

export class FeatureFlagsHelper<TAppName extends keyof ApplicationsFlags> {
    public getFlags: () => ApplicationsFlags[TAppName];

    private staticFlags: ApplicationsFlags[TAppName];

    constructor(private applicationName: TAppName,
        private getFlagsConfigFromState?: () => GetStateReturn,
        private flagData?: FlagData<GetStateReturn[TAppName]>) {
        if (this.flagData) {
            this.setFromStaticValues();
        }
        if (Config.loadFlagsFromSubscriptions && this.getFlagsConfigFromState) {
            this.setFromSubscriptions();
        }
    }

    private setFromStaticValues = () => {
        const environment = getCurrentEnvironment();
        this.staticFlags =
            Object.keys(this.flagData)
                .reduce(
                    (otherFlags, flagName) => ({
                        ...otherFlags,
                        [flagName]: flagIsSet(this.flagData[flagName], environment)
                    }),
                    {}
                ) as ApplicationsFlags[TAppName];
        this.getFlags = () => this.staticFlags;
    }

    private setFromSubscriptions = () => {
        this.getFlags = () => {
            const dynamicFlagsConfig = this.getFlagsConfigFromState();
            const dynamicFlags = dynamicFlagsConfig && dynamicFlagsConfig[this.applicationName];
            return {
                ...(this.staticFlags as {}),
                ...(dynamicFlags as {})
            } as ApplicationsFlags[TAppName];
        };
    }
}
